export interface MenuItem {
  id?: string;
  name: string;
  description: string;
  price: number;
  imageUrl?: string;
  isAvailable: boolean;
  options?: { name: string; price: number }[];
  spicyLevel?: number; // Values: 0 (none), 1 (mild), 2 (medium), 3 (hot), 4 (extreme)
  allergens?: string; // Comma-separated list of allergens
  preparationTime?: number; // In minutes
  calories?: number; // In kcal
  order?: number;
  restaurantId?: string;
  categoryId?: string;
}

export type MenuItemWithCategory = MenuItem & { category: string };

export interface Category {
  id?: string;
  name: string;
  description?: string;
  restaurantId?: string;
  items?: MenuItem[];
  order?: number; // Optional order property for sorting
  isHidden?: boolean; // Optional flag to hide category from menu viewer
}

export interface Restaurant {
  id?: string;
  restaurantName: string;
  description?: string;
  imageUrl?: string;
  businessType: BusinessType;
  address?: string;
  phone: string;
  subdomain?: string;
  ownerPhoneNumber?: string;
  plan?: string;
  categories: Category[];
  restaurantLogo?: string;
  facebook?: string;
  instagram?: string;
  waze?: string;
  whatsapp?: string;
}

export interface CategoryFormData {
  name: string;
  description: string | null;
}

export interface WorkingHour {
  id?: string;
  day: string; // e.g., "Monday", "Tuesday", etc.
  open: string; // e.g., "09:00 AM"
  close: string; // e.g., "05:00 PM"
  isVacation?: boolean;
  restaurantId?: string;
}

export enum BusinessType {
  RESTAURANT = "restaurant",
  CAFE = "cafe",
  BAR = "bar",
  FOOD_TRUCK = "food_truck",
  BAKERY = "bakery",
  OTHER = "other",
}

export enum Role {
  OWNER = "owner",
  ADMIN = "admin",
}

export enum PlanType {
  BASIC = "basic",
  ADVANCED = "advanced",
  PREMIUM = "premium",
  ENTERPRISE = "enterprise",
}

export interface Plan {
  maxCategories: number;
  maxMenuItems: number;
  QRCode: boolean;
  emailSupport: boolean;
  analytics: string;
  workingHours: boolean;
  customDomain: boolean;
  planType: PlanType;
}

export const BasicPlan: Plan = {
  maxCategories: 1,
  maxMenuItems: 5,
  QRCode: true,
  emailSupport: true,
  analytics: "not_available",
  workingHours: false,
  customDomain: false,
  planType: PlanType.BASIC,
};

export const AdvancedPlan: Plan = {
  maxCategories: 3,
  maxMenuItems: 20,
  QRCode: true,
  emailSupport: true,
  analytics: "basic",
  workingHours: true,
  customDomain: true,
  planType: PlanType.ADVANCED,
};

export const PremiumPlan: Plan = {
  maxCategories: 10,
  maxMenuItems: 100,
  QRCode: true,
  emailSupport: true,
  analytics: "advanced",
  workingHours: true,
  customDomain: true,
  planType: PlanType.PREMIUM,
};

export const EnterprisePlan: Plan = {
  maxCategories: -1, // -1 indicates unlimited
  maxMenuItems: -1, // -1 indicates unlimited
  QRCode: true,
  emailSupport: true,
  analytics: "advanced",
  workingHours: true,
  customDomain: true,
  planType: PlanType.ENTERPRISE,
};

export const planMap = {
  [PlanType.BASIC]: BasicPlan,
  [PlanType.ADVANCED]: AdvancedPlan,
  [PlanType.PREMIUM]: PremiumPlan,
  [PlanType.ENTERPRISE]: EnterprisePlan,
};

export interface MenuView {
  id?: string;
  menuId?: string;
  restaurantId: string;
  timestamp: Date;
  visitorId: string;
  date?: string; // Format: YYYY-MM-DD for daily aggregation
}

export interface DailyAnalytics {
  id?: string;
  restaurantId: string;
  date: string; // Format: YYYY-MM-DD
  totalViews: number;
  uniqueVisitors: number;
}
